// Copyright (c) 2024 by Audere

import {
  BaseQueryApi,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";

import { AppDispatch } from "../store";
import { State as AuthState, logOut } from "../slices/authSlice";
import { googleLogout } from "@react-oauth/google";
import { ActionCreatorWithoutPayload } from "@reduxjs/toolkit";
import { clearLabel } from "../slices/labelSlice";
import { LogEventData } from "./logging";

const isProd = () => {
  const host = window.location.hostname;
  return !host.includes(".staging.") && !host.includes("localhost");
};

const apiBaseURL = () => {
  const apiHost = isProd()
    ? "ficus.af-south-1.epistore.auderenow.io"
    : "ficus.af-south-1.epistore.staging.auderenow.io";
  return `https://${apiHost}/v1/ficus`;
};

const nonFatalEndpoints: string[] = [];

const baseQuery = fetchBaseQuery({
  baseUrl: apiBaseURL(),
  mode: "cors",
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    headers.set("Content-Type", "application/json");
    if (!headers.get("X-Audere-API-Token")) {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;
      if (token) {
        headers.set("X-Audere-API-Token", token);
      }
    }
    return headers;
  },
});

export const logOutHelper = (
  dispatch: AppDispatch,
  resetApiState: ActionCreatorWithoutPayload<`${string}/resetApiState`>
) => {
  googleLogout();
  dispatch(logOut());
  dispatch(resetApiState());
  dispatch(clearLabel());
};

const baseQueryWithReauth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object
) => {
  if (api.endpoint !== "openIdAuthenticate") {
    const getStateT = api.getState as () => { auth: AuthState };
    const auth = getStateT().auth;
    const currentGroupRole = auth.persistentGroupRole?.[auth.email];
    const patientGroupRoles = auth.patientGroupRoles;
    if (
      currentGroupRole &&
      patientGroupRoles &&
      (!(currentGroupRole.group in patientGroupRoles) ||
        !patientGroupRoles[currentGroupRole.group].includes(
          currentGroupRole.role
        ))
    ) {
      const refreshResult = {
        error: {
          status: "CUSTOM_ERROR",
          error: "Unauthorized role",
        } as FetchBaseQueryError,
      };
      logOutHelper(api.dispatch, apiSlice.util.resetApiState);
      return refreshResult;
    }

    if (new Date(auth.expires) < new Date(Date.now())) {
      // Token expiry; log out. Possible TODO is to refresh the token.
      console.log("Expired token - logging out");
      const refreshResult = {
        error: {
          status: "CUSTOM_ERROR",
          error: "Token has expired",
        } as FetchBaseQueryError,
      };
      logOutHelper(api.dispatch, apiSlice.util.resetApiState);
      return refreshResult;
    }
  }

  const result = await baseQuery(args, api, extraOptions);
  if (
    result.error?.status === "FETCH_ERROR" &&
    !nonFatalEndpoints.includes(api.endpoint)
  ) {
    console.log(`Fetch error from ${api.endpoint} - logging out`);
    logOutHelper(api.dispatch, apiSlice.util.resetApiState);
  }

  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    logEvent: builder.mutation<string, LogEventData>({
      query: (args) => {
        if (!import.meta.env.VITE_LOG_ID) {
          throw new Error(
            "VITE_LOG_ID is not set so cannot log events. If running locally, add a .env file with this variable included."
          );
        }
        return {
          url: "/portal_log",
          method: "POST",
          body: JSON.stringify({
            timestamp: new Date().toISOString(),
            source: "text-labeler",
            ...args,
          }),
          headers: [["X-Api-Key", `${import.meta.env.VITE_LOG_ID}`]],
        };
      },
    }),
  }),
});

export const logEvent = (dispatch: AppDispatch, event: LogEventData) => {
  void dispatch(apiSlice.endpoints.logEvent.initiate(event));
};

export interface TextLabelerApiResult<T> {
  request_uid: string;
  method_id: string;
  response_code: string;
  output: T;
}
